<template>
  <div class="withdraw">
    <van-nav-bar
      :title="$t('newAdd.Withdraw')"
      fixed
      placeholder
      left-arrow
      @click-left="$router.back()"
    >
      <template #right>
        <div class="withdraw-record-link" @click="$router.push({ name: 'withdrawList' })">
          {{ $t("newAdd.Record") }}
        </div>
      </template>
    </van-nav-bar>

    <div class="withdraw-content">
      <van-form
        ref="withdrawForm"
        :disabled="loading"
        :submit-on-enter="false"
        validate-first
        validate-trigger="onSubmit"
      >
        <div class="withdraw-card">
          <div class="withdraw-card__title">{{ $t("Home.WithdrawSu.Withdrawal") }}</div>
          <van-field
            v-model="formData.money"
            name="money"
            type="number"
            :label="$t('walletPage.amount')"
            :placeholder="$t('newAdd.EnterWithdrawalAmount')"
            :rules="formRules.money"
          />
          <van-field
            readonly
            :value="$money(userInfo.money || 0, false)"
            :label="$t('newAdd.Balance')"
          />
        </div>

        <div class="withdraw-card">
          <div class="withdraw-card__title">{{ $t("profileMenu.bankInfo") }}</div>
          <van-field
            readonly
            :value="bankInfo.name || ''"
            :label="$t('bankAccount.accountName')"
          />
          <van-field
            readonly
            :value="bankInfo.bank"
            :label="$t('bankAccount.bank')"
          />
          <van-field
            readonly
            :value="bankInfo.card || ''"
            :label="$t('bankAccount.accountNumber')"
          />
          <van-field
            v-model="formData.remark"
            class="withdraw-remark"
            name="remark"
            type="textarea"
            rows="3"
            :label="$t('withdrawPage.remark')"
            :placeholder="$t('withdrawPage.remarkPlaceholder')"
          />
        </div>

        <div class="withdraw-submit">
          <van-button
            block
            native-type="button"
            :loading="loading"
            :disabled="loading"
            @click="submit"
          >
            {{ $t("Button.confirm") }}
          </van-button>
        </div>
      </van-form>
    </div>
  </div>
</template>

<script>
import { GET_USERS_BANK, WITHDRAW, userinfo } from "@/api";
import { Button, Dialog, Field, Form, NavBar, Toast } from "vant";
import { mapGetters } from "vuex";

export default {
  name: "BankWithdraw",
  components: {
    [Button.name]: Button,
    [Field.name]: Field,
    [Form.name]: Form,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      loading: false,
      userInfoReady: false,
      bankListReady: false,
      bankInfo: {},
      formData: {
        money: "",
        remark: "",
      },
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
    formRules() {
      return {
        money: [
          {
            required: true,
            message: this.$t("newAdd.EnterWithdrawalAmount"),
          },
          {
            validator: (value) => Number.isFinite(Number(value)) && Number(value) > 0,
            message: this.$t("withdrawPage.invalidAmount"),
          },
          {
            validator: (value) => Number(value) <= Number(this.userInfo.money || 0),
            message: this.$t("Home.WithdrawSu.Insufficient"),
          },
        ],
      };
    },
  },
  created() {
    this.preparePage();
  },
  methods: {
    getErrorMessage(error) {
      if (typeof error === "string") return error;
      return (
        (error && error.response && error.response.data && error.response.data.msg) ||
        (error && error.msg) ||
        this.$t("Hint.failed")
      );
    },
    async loadUserInfo() {
      this.userInfoReady = false;
      try {
        const response = await userinfo();
        const { ret, msg, data } = response.data;
        if (Number(ret) !== 1) {
          Toast(msg || this.$t("Hint.failed"));
          return false;
        }
        this.$store.commit("SET_USERINFO", data || {});
        this.userInfoReady = true;
        return true;
      } catch (error) {
        Toast(this.getErrorMessage(error));
        return false;
      }
    },
    async loadBankAccounts() {
      this.bankListReady = false;
      this.bankInfo = {};
      try {
        const response = await GET_USERS_BANK();
        const { ret, msg, data } = response.data;
        if (Number(ret) !== 1) {
          Toast(msg || this.$t("newAdd.GetBankInfoFail"));
          return false;
        }
        const banks = Array.isArray(data) ? data.filter((item) => !!item) : [];
        this.bankInfo = banks.find((item) => Number(item.default) === 1) || banks[0] || {};
        this.bankListReady = true;
        return true;
      } catch (error) {
        Toast(this.getErrorMessage(error));
        return false;
      }
    },
    async loadData() {
      const [userReady, banksReady] = await Promise.all([
        this.loadUserInfo(),
        this.loadBankAccounts(),
      ]);
      return userReady && banksReady;
    },
    async promptSetup(message, routeName, backOnCancel = false) {
      try {
        await Dialog.confirm({
          title: this.$t("newAdd.warmTitle"),
          message,
          confirmButtonText: this.$t("Button.confirm"),
          cancelButtonText: this.$t("Button.cancle"),
        });
      } catch (error) {
        if (backOnCancel) this.$router.back();
        return false;
      }
      await this.$router.push({ name: routeName });
      return false;
    },
    async ensurePrerequisites() {
      if (this.bankInfo.id == null || this.bankInfo.id === "") {
        return this.promptSetup(this.$t("newAdd.NotHaveCard"), "BankCardAdd", true);
      }
      return true;
    },
    async preparePage() {
      return this.$submitOnce("withdraw", async () => {
        this.loading = true;
        try {
          if (await this.loadData()) {
            await this.ensurePrerequisites();
          }
        } catch (error) {
          Toast(this.getErrorMessage(error));
        } finally {
          this.loading = false;
        }
      });
    },
    async submit() {
      if (this.loading) return;

      return this.$submitOnce("withdraw", async () => {
        this.loading = true;
        try {
          if ((!this.userInfoReady || !this.bankListReady) && !(await this.loadData())) {
            return;
          }
          if (!(await this.ensurePrerequisites())) return;

          try {
            await this.$refs.withdrawForm.validate();
          } catch (errors) {
            return;
          }

          const response = await WITHDRAW({
            money: this.formData.money,
            remark: this.formData.remark,
            bankid: this.bankInfo.id,
          });
          const { ret, msg } = response.data;
          if (Number(ret) !== 1) {
            Toast(msg || this.$t("Hint.failed"));
            return;
          }

          this.formData.remark = "";
          Toast(msg || this.$t("Hint.success"));
          this.$store.dispatch("getUserInfo");
          this.$router.back();
        } catch (error) {
          Toast(this.getErrorMessage(error));
        } finally {
          this.loading = false;
        }
      });
    },
  },
};
</script>

<style lang="less" scoped>
.withdraw {
  width: 100%;
  min-height: 100vh;
  background: var(--page-background);
}

.withdraw-record-link {
  padding: calc(6rem / 16) calc(8rem / 16);
  color: var(--color-primary-strong);
  font-size: calc(12rem / 16);
}

.withdraw-content {
  padding: calc(8rem / 16) calc(14rem / 16) calc(32rem / 16);
}

.withdraw-card {
  overflow: hidden;
  margin-bottom: calc(14rem / 16);
  border-radius: calc(12rem / 16);
  background: #fff;
  box-shadow: 0 calc(3rem / 16) calc(8rem / 16) rgba(0, 0, 0, 0.04);
}

.withdraw-card__title {
  padding: calc(14rem / 16) calc(14rem / 16) calc(5rem / 16);
  color: #666;
  font-size: calc(12rem / 16);
  line-height: 1.5;
}

/deep/ .withdraw-card .van-field {
  padding: calc(8rem / 16) calc(14rem / 16);
  color: #666;
  font-size: calc(14rem / 16);
  line-height: 1.5;
}

/deep/ .withdraw-card .van-field__label {
  flex: none;
  width: calc(104rem / 16);
  margin-right: calc(12rem / 16);
  color: #666;
  line-height: 1.5;
}

/deep/ .withdraw-card .van-field__control {
  color: #666;
  line-height: 1.5;
}

/deep/ .withdraw-card .van-field__control::placeholder {
  color: #666;
}

/deep/ .withdraw-card .withdraw-remark .van-field__control {
  height: 4.5em;
  min-height: 0;
  overflow-y: auto;
}

/deep/ .withdraw-card .van-field__error-message {
  margin-top: calc(4rem / 16);
  line-height: 1.5;
}

/deep/ .withdraw-card .van-cell:not(:last-child)::after {
  right: 0;
  left: 0;
  border-color: #f2f2f2;
}

.withdraw-submit {
  margin: calc(18rem / 16) calc(10rem / 16) 0;
}

/deep/ .withdraw-submit .van-button {
  height: auto;
  padding: calc(14rem / 16) calc(16rem / 16);
  border: 0;
  border-radius: calc(7rem / 16);
  background: var(--button-background);
  box-shadow: var(--card-shadow);
  color: var(--button-text);
  font-size: calc(18rem / 16);
  font-weight: 600;
  line-height: 1.3;
}
</style>
